/**
 * Tint removal for AI-staged images.
 *
 * Gemini occasionally introduces a global colour cast — most commonly a
 * magenta/purple wash — when staging or re-editing real-estate photos. The
 * drift compounds when the user iterates on the same image (each pass feeds
 * the previous, already-tinted output back in). Prompt-based guardrails help
 * but do not fully eliminate it.
 *
 * This module neutralises the cast in post-processing by anchoring the
 * staged image's white point to *known-neutral* pixels in the source.
 *
 * Algorithm (single pass, fully client-side, no dependencies):
 *   1. Downsample source + staged into a small calibration grid.
 *   2. Find pixels in the SOURCE that are near-white AND near-neutral
 *      (high luminance, low chroma) — typically walls, ceilings, white trim.
 *   3. At the SAME coordinates in the staged image, compute the per-channel
 *      mean.
 *   4. Derive per-channel gains so the staged means match the source means
 *      at those anchor pixels.
 *   5. Apply the gains uniformly to the full-resolution staged image.
 *
 * Safety rails:
 *   - Bail out (return the staged image untouched) if fewer than
 *     `MIN_ANCHOR_PIXELS` reliable anchors are found (e.g. a fully dark room
 *     with no white surfaces).
 *   - Clamp every channel gain to `[GAIN_CLAMP_MIN, GAIN_CLAMP_MAX]` so a
 *     pathological estimate cannot blow out colours.
 *   - Skip the apply step entirely if all gains are within `GAIN_DEADZONE`
 *     of 1.0 (no meaningful drift to correct).
 */

const SAMPLE_MAX_DIM = 256;
// Lowered from 200 → 170 so we still pick up anchors in rooms whose walls
// are mid-tone (warm cream, beige, soft greige) rather than bright white.
const NEAR_WHITE_LUMA_MIN = 170; // 0..255 (Rec.601 luma)
// Loosened from 25 → 45 so off-white / cream / light-beige walls and
// ceilings still register as "near-neutral" anchors. Without this, rooms
// with warm decor produced almost no anchors and the corrector silently
// no-op'd while Gemini drifted toward magenta/red.
const MAX_CHROMA_FOR_NEUTRAL = 45; // max(R,G,B) - min(R,G,B)
const MIN_ANCHOR_PIXELS = 50;
// Loosened from ±15% → ±25%. Iterative staging passes occasionally drift
// further than 15% on a single channel, especially in warm-toned rooms,
// and the old clamp was leaving residual tint that compounded.
const GAIN_CLAMP_MIN = 0.75;
const GAIN_CLAMP_MAX = 1.25;
const GAIN_DEADZONE = 0.01; // ignore corrections smaller than ±1%

// Gray-world fallback: when there aren't enough near-neutral anchors,
// assume the *average chromaticity* of the staged image should match the
// source. Used only as a fallback because it can be biased by large
// coloured objects (e.g. a giant green sofa Gemini just added) — so we
// clamp it harder than the anchor-based gain.
const GRAY_WORLD_GAIN_CLAMP_MIN = 0.88;
const GRAY_WORLD_GAIN_CLAMP_MAX = 1.12;

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    if (!src.startsWith('data:')) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });

const drawScaled = (
  img: HTMLImageElement,
  w: number,
  h: number
): ImageData | null => {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h);
};

export interface RemoveTintResult {
  /** Final data URL — corrected if a tint was detected, original otherwise. */
  dataUrl: string;
  /** Per-channel gain that was applied (1,1,1 if no correction). */
  gains: { r: number; g: number; b: number };
  /** Number of anchor pixels used. 0 if no correction was applied. */
  anchorCount: number;
  /** Which estimator drove the correction. */
  method: 'anchors' | 'gray-world' | 'none';
}

/** Median of a numeric array. Mutates `arr` (sorts in place). */
const median = (arr: number[]): number => {
  if (arr.length === 0) return 1;
  arr.sort((a, b) => a - b);
  const mid = arr.length >> 1;
  return arr.length % 2 === 0 ? (arr[mid - 1] + arr[mid]) / 2 : arr[mid];
};

/**
 * Remove a global colour cast from `stagedDataUrl` by matching its white
 * point to neutral highlights in `sourceDataUrl`. Returns the corrected
 * data URL (PNG) plus diagnostic info. Never throws; returns the staged
 * image unchanged on any failure.
 */
export const removeGlobalTint = async (
  sourceDataUrl: string,
  stagedDataUrl: string
): Promise<RemoveTintResult> => {
  const passthrough: RemoveTintResult = {
    dataUrl: stagedDataUrl,
    gains: { r: 1, g: 1, b: 1 },
    anchorCount: 0,
    method: 'none',
  };

  try {
    const [src, staged] = await Promise.all([
      loadImage(sourceDataUrl),
      loadImage(stagedDataUrl),
    ]);

    if (
      src.width === 0 || src.height === 0 ||
      staged.width === 0 || staged.height === 0
    ) {
      return passthrough;
    }

    // Calibration canvas: same content for source and staged, mapped to a
    // shared low-res grid so we can compare per-pixel without an alignment
    // step. We use the staged image's aspect ratio because that's what we'll
    // ultimately apply the gains to.
    const aspect = staged.width / staged.height;
    let cw = SAMPLE_MAX_DIM;
    let ch = Math.max(1, Math.round(SAMPLE_MAX_DIM / aspect));
    if (ch > SAMPLE_MAX_DIM) {
      ch = SAMPLE_MAX_DIM;
      cw = Math.max(1, Math.round(SAMPLE_MAX_DIM * aspect));
    }

    const srcData = drawScaled(src, cw, ch);
    const stagedData = drawScaled(staged, cw, ch);
    if (!srcData || !stagedData) return passthrough;

    // Collect PER-PIXEL gain ratios (source / staged) at every neutral
    // anchor. Using the median of these ratios is dramatically more robust
    // than mean-of-sums when:
    //  - some anchor coords got covered by furniture in the staged image
    //    (those pixels are now sofa-coloured, not wall-coloured) — they
    //    become outliers that the median ignores
    //  - the wall colour varies across the room (shadow vs lit side) —
    //    each pixel's local ratio still encodes the global cast
    const ratiosR: number[] = [];
    const ratiosG: number[] = [];
    const ratiosB: number[] = [];

    // Gray-world accumulators (used as fallback when neutral anchors are
    // sparse — e.g. a fully wood-panelled room).
    let gwSumSrcR = 0, gwSumSrcG = 0, gwSumSrcB = 0;
    let gwSumStgR = 0, gwSumStgG = 0, gwSumStgB = 0;
    let gwCount = 0;

    const pxs = srcData.data;
    const pxt = stagedData.data;
    for (let i = 0; i < pxs.length; i += 4) {
      const sr = pxs[i], sg = pxs[i + 1], sb = pxs[i + 2];
      const tr = pxt[i], tg = pxt[i + 1], tb = pxt[i + 2];

      // Gray-world: every reasonably-bright, non-saturated pixel
      // contributes to the average chromaticity.
      const sLuma = 0.299 * sr + 0.587 * sg + 0.114 * sb;
      if (sLuma >= 40 && sLuma <= 245) {
        gwSumSrcR += sr; gwSumSrcG += sg; gwSumSrcB += sb;
        gwSumStgR += tr; gwSumStgG += tg; gwSumStgB += tb;
        gwCount++;
      }

      // Neutral-anchor selection (source side only — we trust the source
      // to know what was meant to be neutral).
      if (sLuma < NEAR_WHITE_LUMA_MIN) continue;
      const maxC = Math.max(sr, sg, sb);
      const minC = Math.min(sr, sg, sb);
      if (maxC - minC > MAX_CHROMA_FOR_NEUTRAL) continue;

      // Avoid divide-by-zero on the staged side.
      if (tr < 2 || tg < 2 || tb < 2) continue;

      ratiosR.push(sr / tr);
      ratiosG.push(sg / tg);
      ratiosB.push(sb / tb);
    }

    const anchorCount = ratiosR.length;
    let gainR: number, gainG: number, gainB: number;
    let method: RemoveTintResult['method'];

    if (anchorCount >= MIN_ANCHOR_PIXELS) {
      const clamp = (g: number) =>
        Math.max(GAIN_CLAMP_MIN, Math.min(GAIN_CLAMP_MAX, g));
      gainR = clamp(median(ratiosR));
      gainG = clamp(median(ratiosG));
      gainB = clamp(median(ratiosB));
      method = 'anchors';
    } else if (gwCount >= MIN_ANCHOR_PIXELS) {
      // Fallback: gray-world chromaticity match. Equalise the average
      // colour of the staged scene to the average colour of the source.
      // Clamped harder because large coloured furniture additions can
      // legitimately shift the average.
      if (gwSumStgR < 1 || gwSumStgG < 1 || gwSumStgB < 1) {
        return { ...passthrough, anchorCount };
      }
      const meanSrcR = gwSumSrcR / gwCount;
      const meanSrcG = gwSumSrcG / gwCount;
      const meanSrcB = gwSumSrcB / gwCount;
      const meanStgR = gwSumStgR / gwCount;
      const meanStgG = gwSumStgG / gwCount;
      const meanStgB = gwSumStgB / gwCount;
      const clampGW = (g: number) =>
        Math.max(GRAY_WORLD_GAIN_CLAMP_MIN, Math.min(GRAY_WORLD_GAIN_CLAMP_MAX, g));
      gainR = clampGW(meanSrcR / meanStgR);
      gainG = clampGW(meanSrcG / meanStgG);
      gainB = clampGW(meanSrcB / meanStgB);
      method = 'gray-world';
    } else {
      return { ...passthrough, anchorCount };
    }

    if (
      Math.abs(gainR - 1) < GAIN_DEADZONE &&
      Math.abs(gainG - 1) < GAIN_DEADZONE &&
      Math.abs(gainB - 1) < GAIN_DEADZONE
    ) {
      return {
        dataUrl: stagedDataUrl,
        gains: { r: gainR, g: gainG, b: gainB },
        anchorCount,
        method,
      };
    }

    const out = document.createElement('canvas');
    out.width = staged.width;
    out.height = staged.height;
    const octx = out.getContext('2d', { willReadFrequently: true });
    if (!octx) return passthrough;
    octx.imageSmoothingEnabled = false;
    octx.drawImage(staged, 0, 0);

    const id = octx.getImageData(0, 0, out.width, out.height);
    const data = id.data;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i] * gainR;
      const g = data[i + 1] * gainG;
      const b = data[i + 2] * gainB;
      data[i]     = r > 255 ? 255 : r < 0 ? 0 : r;
      data[i + 1] = g > 255 ? 255 : g < 0 ? 0 : g;
      data[i + 2] = b > 255 ? 255 : b < 0 ? 0 : b;
    }
    octx.putImageData(id, 0, 0);

    return {
      dataUrl: out.toDataURL('image/png'),
      gains: { r: gainR, g: gainG, b: gainB },
      anchorCount,
      method,
    };
  } catch (err) {
    console.warn('removeGlobalTint failed:', err);
    return passthrough;
  }
};

/**
 * Convenience: returns true if the prompt represents a deliberate
 * recolouring (e.g. "Change ONLY the colour of the {{TARGET}} to {{COLOUR}}").
 * Tint-removal must NOT run for these flows because the user explicitly
 * wants a colour shift.
 */
export const isRecolorPrompt = (text: string): boolean =>
  /Change ONLY the color/i.test(text);
